import { Observable, throwError, timer } from 'rxjs'
import { retry } from 'rxjs/operators'

/**
 * True for an HTTP 4xx. The error interceptor spreads `HttpErrorResponse` into a plain object
 * (`{ ...error, response: { status } }`), so this duck-types `status` instead of using `instanceof`.
 */
const isClientError = (error: unknown): boolean => {
  const candidate = error as { status?: unknown; response?: { status?: unknown } } | null
  const status = candidate?.status ?? candidate?.response?.status
  return typeof status === 'number' && status >= 400 && status < 500
}

/**
 * Retries a failed request `maxRetry` times, `delayMs` apart. A 4xx is not retried (DRRWEB-3662):
 * the server has already given a definitive answer, so repeating the request only spends
 * background calls no member can ever see the result of. Everything else — 5xx, network errors,
 * timeouts — is retried as before.
 */
export function delayedRetry(delayMs = 1000, maxRetry = 3) {
  return (src: Observable<any>) =>
    src.pipe(
      retry({
        count: maxRetry,
        delay: (error) => (isClientError(error) ? throwError(() => error) : timer(delayMs)),
      })
    )
}
